import { DeductionListJson } from "@/api"
import { TableState } from "@/deduction_table/table_state"
import { currencyFormatter } from "@/utils"
import { BarList } from "@tremor/react"
import { aging_row_amount } from "@/utils/deduction_age"
import { format_deduction_status_label } from "@/utils/deduction_status_labels"
import { notifyChartFilterApplied } from "@/utils/filter_feedback"
import { useMemo } from "preact/compat"
import { Bar2 } from "@/deduction/TableBreakdowns.tsx"

type DeductionCategoryData = {
  name: string
  value: number
  amount: number
}

const colors = [
  "bg-green-500",
  "bg-yellow-500",
  "bg-amber-500",
  "bg-red-500",
  "bg-lime-500",
  "bg-sky-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-fuchsia-500",
  "bg-teal-500",
]

export const STATUS_COLORS: { [key: string]: string } = {
  // needs action - red / amber
  new: "red-500",
  backup_received: "amber-500",
  review: "amber-500",
  disputable: "amber-500",

  // partial action taken - yellow
  task_assigned: "yellow-500",
  expensable: "yellow-500",
  backup_requested: "yellow-500",
  disputed: "yellow-500",
  pending: "yellow-500",

  // don't need to work on it - slate (with white text)
  processing: "slate-500 text-white",
  archived: "slate-500 text-white",
  lost: "slate-500 text-white",
  misc: "slate-500 text-white",

  // celebration - green
  validated: "green-500",
  won: "green-500",
}

export const SORT_ORDER = {
  // needs action - red / amber
  new: 0,
  backup_received: 1,
  review: 2,
  disputable: 3,
  // partial action taken - yellow
  task_assigned: 4,
  expensable: 5,
  backup_requested: 6,
  pending: 7,
  disputed: 8,
  // don't need to work on it - slate
  processing: 9,
  archived: 10,
  lost: 11,
  misc: 12,
  // celebration - green
  validated: 13,
  won: 14,
}

export type Bar<T> = T & {
  key?: string
  value: number
  // name: React$1.ReactNode;
  // icon?: React$1.JSXElementConstructor<any>;
  href?: string
  target?: string
  // color?: Color$1;
}

interface StatusBreakdownProps {
  data: DeductionListJson[]
}

export function tallyData(data: DeductionListJson[]): Bar2[] {
  let total = data.length
  let nudge = total * 0.3
  // Get all unique status values from rawData
  let statuses = [...new Set(data.map(row => row.status_value))]
  // Also include 'won' if any row has won_amount (even if no row has status_value='won')

  let acc = Object.fromEntries(statuses.map(v => [v, { count: 0, amount: 0 }]))
  for (let row of data) {
    let entry = acc[row.status_value]
    // skip adding 'new' invoices to the total
    entry.count += 1
    // Shared with the aging panel so the two charts agree on the same rows.
    entry.amount += aging_row_amount(row)
  }
  return Object.entries(acc).map(
    ([key, value]) =>
      ({
        key,
        amount: value.amount,
        name: `${format_deduction_status_label(key)} (${value.count})`,
        value: value.count + nudge,
        color: STATUS_COLORS[key] || "gray",
      }) as Bar2
  )
}

export function StatusBreakdown({ data: rawData }: StatusBreakdownProps) {
  let data = useMemo(() => {
    if (!rawData) return []
    let data = tallyData(rawData)
    data.sort((a, b) => SORT_ORDER[a.key as keyof typeof SORT_ORDER] - SORT_ORDER[b.key as keyof typeof SORT_ORDER])
    return data
  }, [rawData])

  return (
    <div class="flex">
      <BarList
        data={data}
        className="max-w-2xl grow"
        sortOrder="none"
        valueFormatter={(_: DeductionCategoryData) => ""}
        onValueChange={(item: Bar<DeductionCategoryData>) => {
          TableState.set(s => {
            return {
              ...s,
              search: "",
              earlyPay: false,
              // transactionTypes: ["deduction", "invoice"],
              columnFilters: [{ id: "status_value", value: item.key! }],
              pagination: { pageIndex: 0, pageSize: s.pagination.pageSize },
            }
          })
          notifyChartFilterApplied(`Showing deductions in stage "${format_deduction_status_label(item.key!)}".`)
        }}
      />
      <div class="xl:ml-6">
        {data.map((d, i) => {
          return <div class="h-8 mb-1.5 items-center flex">{currencyFormatter(Math.abs(d.amount))}</div>
        })}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function makeDeduction(overrides: Partial<DeductionListJson>): DeductionListJson {
    return {
      id: "test-id",
      org_id: "org-1",
      remote_id: "remote-1",
      created_at: "2024-01-01",
      updated_at: "2024-01-01",
      source: "test",
      invoice_amount: 100,
      discount_amount: 0,
      kind: "deduction",
      gross_amount: 100,
      invoice_date: "2024-01-01",
      invoice_number: "INV-001",
      check_amount: 100,
      check_date: "2024-01-01",
      check_number: "CHK-001",
      category_id: "cat-1",
      backup_s3_uri: null,
      check_s3_uri: null,
      data: "{}",
      document: "",
      retailer_name: null,
      execution_date: "2024-01-01",
      user_files: [],
      dispute_id: "",
      product_line: "",
      retailer_invoice_number: "",
      has_splits: false,
      is_parent: false,
      import_source: "test",
      pending: false,
      category: "test",
      category_description: "",
      status_value: "new",
      customer_name: "Test Customer",
      deduction_amount: 100,
      validated: false,
      has_notes: false,
      has_backup: false,
    } as DeductionListJson
  }

  describe("tallyData", () => {
    it("should tally disputed row with won_amount in both disputed and won sections", () => {
      const data: DeductionListJson[] = [
        {
          ...makeDeduction({}),
          status_value: "disputed",
          invoice_number: "SASIF123",
          won_amount: 100,
        },
      ]

      const result = tallyData(data)

      const disputed = result.find(r => r.key === "disputed")!

      // The row should be counted in the disputed section
      expect(disputed)
      expect(disputed!.amount).toBe(100)
    })

    it("should use aggregated dispute_amount for disputed totals", () => {
      const data: DeductionListJson[] = [
        {
          ...makeDeduction({}),
          status_value: "disputed",
          dispute_amount: 250,
          deduction_amount: 100,
        },
      ]

      const result = tallyData(data)
      const disputed = result.find(r => r.key === "disputed")!
      expect(disputed!.amount).toBe(250)
    })

    it("should use discount-backed deduction_amount for disputable early-pay invoices, not inflated dispute_amount", () => {
      const data: DeductionListJson[] = [
        {
          ...makeDeduction({}),
          kind: "invoice",
          status_value: "disputable",
          invoice_amount: -5000,
          discount_amount: 100,
          deduction_amount: 100,
          dispute_amount: 5000,
        },
      ]

      const result = tallyData(data)
      const disputable = result.find(r => r.key === "disputable")!
      expect(disputable!.amount).toBe(100)
    })
  })
}
